// Configuración optimizada de axios para evitar problemas con ALB
import axios from 'axios';

// Crear una instancia de axios con configuración optimizada para ALB
const createOptimizedAxios = () => {
  const instance = axios.create({
    // Configuraciones optimizadas para ALB
    timeout: 30000, // 30 segundos timeout
    
    // Headers seguros para navegadores
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      // Indicar al servidor que esperamos una respuesta
      'Cache-Control': 'no-cache'
    },
    
    // Configuración de retry automático para manejar problemas temporales del ALB
    validateStatus: function (status) {
      // Considerar como exitosas las respuestas 2xx y 3xx
      return status >= 200 && status < 400;
    },
    
    // Configuración de proxy - importante para ALB
    proxy: false,
    
    // Configuración para manejar redirecciones del ALB
    maxRedirects: 5,
    
    // Configuración para evitar problemas de SSL con certificados autofirmados en desarrollo
    httpsAgent: undefined, // Dejar que el navegador maneje SSL
    
    // Configuración de transformación de respuesta para debugging
    transformResponse: [function (data) {
      if (typeof data === 'string') {
        try {
          return JSON.parse(data);
        } catch (e) {
          console.warn('⚠️ Respuesta no es JSON válido:', data);
          return data;
        }
      }
      return data;
    }]
  });

  // Interceptor de request para logging
  instance.interceptors.request.use(
    (config) => {
      console.log(`🔄 ${config.method?.toUpperCase()} ${config.url}`);
      
      // Solo logging, no modificamos headers inseguros
      return config;
    },
    (error) => {
      console.error('❌ Error en request interceptor:', error);
      return Promise.reject(error);
    }
  );

  // Interceptor de response para manejo de errores y logging
  instance.interceptors.response.use(
    (response) => {
      console.log(`✅ ${response.status} ${response.config.url}`);
      return response;
    },
    (error) => {
      // Logging detallado de errores para debug de ALB
      if (error.response) {
        console.error(`❌ Error ${error.response.status}: ${error.response.config?.url}`);
        console.error('Response data:', error.response.data);
      } else if (error.request) {
        console.error('❌ Error de red - sin respuesta del servidor:', error.request);
        console.error('Posible problema de ALB o conectividad');
      } else {
        console.error('❌ Error configurando request:', error.message);
      }
      
      // Retry automático para errores 5xx que pueden ser temporales del ALB
      if (error.response?.status >= 500 && error.response?.status < 600) {
        console.log('🔄 Reintentando request por error 5xx...');
        // El retry se puede implementar aquí si es necesario
      }
      
      return Promise.reject(error);
    }
  );

  return instance;
};

// Instancia global optimizada
export const api = createOptimizedAxios();

// Función helper para realizar requests con retry automático
export const makeRequest = async (config, retries = 2) => {
  for (let i = 0; i <= retries; i++) {
    try {
      const response = await api(config);
      return response;
    } catch (error) {
      console.log(`❌ Intento ${i + 1}/${retries + 1} falló:`, error.message);
      
      if (i === retries) {
        throw error;
      }
      
      // Esperar antes del retry (exponential backoff)
      const delay = Math.pow(2, i) * 1000;
      console.log(`⏱️ Esperando ${delay}ms antes del retry...`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
};

// Función helper para requests con autenticación y renovación automática de token
export const makeAuthenticatedRequest = async (config, token, retries = 2) => {
  const authenticatedConfig = {
    ...config,
    headers: {
      ...config.headers,
      'Authorization': `Bearer ${token}`
    }
  };
  
  try {
    return await makeRequest(authenticatedConfig, retries);
  } catch (error) {
    // Si recibimos un 401, intentar renovar el token automáticamente
    if (error.response?.status === 401) {
      console.log('🔄 Error 401 detectado, intentando renovar token...');
      
      try {
        // Intentar renovar el token usando Keycloak
        const refreshResult = await refreshToken();
        
        if (refreshResult.success) {
          console.log('✅ Token renovado exitosamente, reintentando request...');
          
          // Reintentar el request con el nuevo token
          const newAuthenticatedConfig = {
            ...config,
            headers: {
              ...config.headers,
              'Authorization': `Bearer ${refreshResult.newToken}`
            }
          };
          
          return await makeRequest(newAuthenticatedConfig, 0); // Sin retries adicionales
        } else {
          // Error específico en la renovación del token
          const errorMsg = refreshResult.error || 'No se pudo renovar el token';
          console.error('❌ No se pudo renovar el token:', errorMsg);
          
          if (refreshResult.needsReauth) {
            console.error('🔑 Se requiere reautenticación del usuario');
            // Crear un error específico para reautenticación
            const reAuthError = new Error('Se requiere reautenticación');
            reAuthError.needsReauth = true;
            reAuthError.originalError = error;
            throw reAuthError;
          }
          
          // Propagar el error original si no es un problema de reauth
          throw error;
        }
      } catch (refreshError) {
        // Si refreshError es nuestro error de reauth, propagarlo
        if (refreshError.needsReauth) {
          throw refreshError;
        }
        
        // Para otros errores de renovación, loggear y propagar el error original
        const errorMsg = refreshError?.message || refreshError?.toString() || 'Error desconocido en renovación';
        console.error('❌ Error renovando token:', errorMsg);
        
        // Propagar el error original del request, no el de renovación
        throw error;
      }
    }
    
    // Para cualquier otro error, propagarlo normalmente
    throw error;
  }
};

// Función para renovar el token usando Keycloak
let keycloakInstance = null;

export const setKeycloakInstance = (instance) => {
  keycloakInstance = instance;
};

const refreshToken = async () => {
  if (!keycloakInstance) {
    console.error('❌ Instancia de Keycloak no disponible para renovar token');
    return { 
      success: false, 
      error: 'Instancia de Keycloak no disponible' 
    };
  }
  
  try {
    console.log('🔄 Intentando renovar token con Keycloak...');
    console.log('📊 Estado de Keycloak:', {
      authenticated: keycloakInstance.authenticated,
      tokenExpired: keycloakInstance.isTokenExpired(),
      refreshToken: keycloakInstance.refreshToken ? 'presente' : 'ausente',
      token: keycloakInstance.token ? 'presente' : 'ausente'
    });
    
    // Verificar si Keycloak está inicializado correctamente
    if (!keycloakInstance.authenticated) {
      console.error('❌ Usuario no está autenticado en Keycloak');
      return { 
        success: false, 
        error: 'Usuario no autenticado',
        needsReauth: true 
      };
    }
    
    // Intentar actualizar el token (keycloak.updateToken(30) devuelve true si se renovó)
    const refreshed = await keycloakInstance.updateToken(30); // 30 segundos de margen
    
    if (refreshed) {
      console.log('✅ Token renovado exitosamente por Keycloak');
      return { 
        success: true, 
        newToken: keycloakInstance.token,
        wasRefreshed: true
      };
    } else {
      console.log('ℹ️ Token aún válido, no necesita renovación');
      return { 
        success: true, 
        newToken: keycloakInstance.token,
        wasRefreshed: false
      };
    }
  } catch (error) {
    // Mejorar el manejo de errores para evitar "undefined"
    console.log('🔍 Analizando error de renovación:', {
      errorType: typeof error,
      isError: error instanceof Error,
      hasMessage: 'message' in error,
      hasName: 'name' in error,
      keys: Object.keys(error || {}),
      errorValue: error
    });
    
    const errorMessage = error?.message || error?.toString() || JSON.stringify(error) || 'Error completamente desconocido en renovación de token';
    const errorDetails = {
      message: errorMessage,
      name: error?.name || 'UnknownError',
      type: typeof error,
      keys: Object.keys(error || {}),
      stack: error?.stack || 'No stack trace available'
    };
    
    console.error('❌ Error renovando token con Keycloak:', errorDetails);
    
    // Verificar si es un error de autenticación que requiere relogin
    if (errorMessage.includes('Failed to refresh token') || 
        errorMessage.includes('Token expired') ||
        errorMessage.includes('invalid_grant')) {
      return { 
        success: false, 
        error: errorMessage,
        needsReauth: true 
      };
    }
    
    return { 
      success: false, 
      error: errorMessage 
    };
  }
};

export default api;
